/**
 * @module app.shared
 */

import { autoinject } from 'aurelia-framework';
import { IHtmlValue, HtmlValue, escapeHtml } from 'app/shared/lib/formatting/html-utilities';
import { I18N } from 'aurelia-i18n';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { ITranslation, translate } from 'app/shared/lib/i18n';
import { guid } from 'app/shared/lib/guid';

export type ToastType = 'info' | 'warn' | 'error' | 'default';
export type ToastDuration = 'default' | 'long' | 'short' | 'indefinite';

@autoinject
export class Toaster {

    public readonly standard: StandardToasts = new StandardToasts(this);

    constructor(private readonly i18n: I18N, private readonly logger: AppLogger) {
        this.logger.source = this;
        this.logger.target = 'console';
    }

    /**
     * Dismiss all visible toasts
     */
    public dismissAll(): void {
        M.Toast.dismissAll();
    }

    /**
     * Create an "info" Toast message.
     */
    public info(): Toast {
        return new Toast('info', this.i18n, this.logger)
            .title(true)
            .class('ess-toast-info')
            .icon('ui:icons.info');
    }

    /**
     * Create "warn" Toast message.
     */
    public warn(): Toast {
        return new Toast('warn', this.i18n, this.logger)
            .title(true)
            .class('ess-toast-warning')
            .icon('ui:icons.warning');
    }

    /**
     * Create an "error" Toast message.
     */
    public error(): Toast {
        return new Toast('error', this.i18n, this.logger)
            .title(true)
            .class('ess-toast-error')
            .icon('ui:icons.error');
    }

    /**
     * Create a custom Toast message.
     */
    public toast(): Toast {
        return new Toast('default', this.i18n, this.logger)
    }

}

export class StandardToasts {

    constructor(private readonly toaster: Toaster) { }

    public activate(modelKey: string, isActive: boolean, succeeded: boolean = true, count?: string | number): Promise<string> {
        let key: string;
        if(isActive) {
            key = succeeded ? 'ui:messages.events.activated' : 'ui:messages.events.activateFailed'
        } else {
            key = succeeded ? 'ui:messages.events.deactivated' : 'ui:messages.events.deactivateFailed'
        }

        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: key,
            count: count,
            what: modelKey
        }).show();
    }

    public added(modelKey: string, succeeded: boolean = true, count?: string | number): Promise<string> {
        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: succeeded ? 'ui:messages.events.added' : 'ui:messages.events.addFailed',
            count: count,
            what: modelKey
        }).show();
    }

    public assigned(modelKey: string, succeeded: boolean = true, count?: string | number): Promise<string> {
        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: succeeded ? 'ui:messages.events.assigned' : 'ui:messages.events.assignFailed',
            count: count,
            what: modelKey
        }).show();
    }

    public completed(modelKey: string, succeeded: boolean = true, count?: string | number): Promise<string> {
        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: succeeded ? 'ui:messages.events.completed' : 'ui:messages.events.completeFailed',
            count: count,
            what: modelKey
        }).show();
    }

    public canceled(modelKey: string, succeeded: boolean = true, count?: string | number): Promise<string> {
        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: succeeded ? 'ui:messages.events.canceled' : 'ui:messages.events.canceledFailed',
            count: count,
            what: modelKey
        }).show();
    }

    public created(modelKey: string, succeeded: boolean = true, count?: string | number): Promise<string> {
        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: succeeded ? 'ui:messages.events.created' : 'ui:messages.events.createFailed',
            count: count,
            what: modelKey
        }).show();
    }

    public deleted(modelKey: string, succeeded: boolean = true, count?: string | number): Promise<string> {
        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: succeeded ? 'ui:messages.events.deleted' : 'ui:messages.events.deleteFailed',
            count: count,
            what: modelKey
        }).show();
    }

    public invalidRequired(): Promise<string> {
        return this.toaster.warn().message({
            key: 'ui:messages.events.invalidRequired'
        }).show();
    }

    public routed(modelKey: string, succeeded: boolean = true, count?: string | number): Promise<string> {
        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: succeeded ? 'ui:messages.events.routed' : 'ui:messages.events.routeFailed',
            count: count,
            what: modelKey
        }).show();
    }

    public returned(modelKey: string, succeeded: boolean = true, count?: string | number): Promise<string> {
        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: succeeded ? 'ui:messages.events.returned' : 'ui:messages.events.returnFailed',
            count: count,
            what: modelKey
        }).show();
    }

    public saved(modelKey: string, succeeded: boolean = true, count?: string | number): Promise<string> {
        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: succeeded ? 'ui:messages.events.saved' : 'ui:messages.events.saveFailed',
            count: count,
            what: modelKey
        }).show();
    }

    public sorted(modelKey: string, succeeded: boolean = true): Promise<string> {
        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: succeeded ? 'ui:messages.events.sorted' : 'ui:messages.events.sortFailed',
            what: modelKey
        }).show();
    }

    /**
     * Creates a batch progress toaster
     * @param modelKey
     * @param count
     * @returns
     */
    public batch(modelKey: string, count: number): Toast {
        const toast = this.toaster.info().duration('indefinite').progress(count).message({
            key: 'ui:messages.events.saveProgress',
            count: count,
            what: modelKey
        });
        return toast;
    }

    public searchNoCriteria(modelKey?: string): Promise<string> {
        return this.toaster.warn().message({
            key: 'ui:messages.search.noCriteria',
            what: modelKey
        }).show();
    }

    public searchNoResults(modelKey?: string): Promise<string> {
        return this.toaster.info().message({
            key: 'ui:messages.search.noResults',
            what: modelKey
        }).show();
    }

    public success(): Promise<string> {
        const toast = this.toaster.info();
        return toast.message({
            key: 'ui:messages.events.success'
        }).show();
    }

    public updated(modelKey: string, succeeded: boolean = true, count?: string | number): Promise<string> {
        const toast = succeeded ? this.toaster.info() : this.toaster.error();
        return toast.message({
            key: succeeded ? 'ui:messages.events.updated' : 'ui:messages.events.updateFailed',
            count: count,
            what: modelKey
        }).show();
    }

    public notImplemented(): Promise<string> {
        return this.notImplementedInternal();
    }

    private async notImplementedInternal(): Promise<string> {
        const result = await this.toaster.error()
            .message('This feature has not yet been implemented.')
            .action('vent', 'Get to Work!')
            .show();
        if(result === 'vent') {
            this.toaster.info()
                .message("Roger that! We'll get right on it! Over and out.")
                .show();
        }
        return result;
    }

}

interface IToastAction {
    text: string,
    result: string,
    classes: string[]
}

interface IToastOptions {
    id: string;
    type: ToastType;
    title: string | boolean;
    body: IHtmlValue;
    progress: boolean;
    currentStep: number;
    totalSteps: number;
    message: string;
    icon: string;
    classes: string[];
    actions: IToastAction[];
    duration: number;
    defaultResult: string;
    dismissButton: boolean;
    duplicateInLog: boolean;
}

export class Toast {

    private readonly durations = {
        default: 5000, // 5s
        short: 3000, // 3s
        long: 15000, // 15s
        indefinite: 43200000, // 12h
    };

    private readonly options: IToastOptions = {
        id: guid(),
        type: 'default',
        title: false,
        body: HtmlValue.Empty,
        message: '',
        icon: '',
        classes: [],
        actions: [],
        progress: false,
        currentStep: 0,
        totalSteps: 0,
        duration: this.durations.default,
        defaultResult: 'dismiss',
        dismissButton: true,
        duplicateInLog: true
    };

    public result: string = '';
    private instances: M.Toast[] = [];

    constructor(private type: ToastType, private readonly i18n: I18N, private readonly logger: AppLogger) {
        this.options.type = type;
    }

    /**
     * Dismiss any messages shown by a call to `show()` on this Toast.
     */
    public dismiss(): this {
        for(const instance of this.instances) {
            instance.dismiss();
        }
        return this;
    }

    /**
     * Set the title of this Toast (or an object providing translation details)
     * @param title the title of the Toast (use `true` for the default title)
     */
    public title(title: string | ITranslation | boolean): this {
        if(typeof title === 'string' || typeof title === 'boolean') {
            this.options.title = title;
        }
        else {
            this.options.title = translate(this.i18n, title);
        }
        return this;
    }

    /**
     * Set the message for this Toast (or an object providing translation details)
     * @param message the message of the Toast
     */
    public message(message: string | ITranslation): this {
        if(typeof message === 'string') {
            this.options.message = message;
        }
        else {
            this.options.message = translate(this.i18n, message);
        }
        return this;
    }

    /**
     * Set the **HTML** body of this Toast (advanced usage only)
     * @param body the `IHtmlValue` containing the **HTML** to render in this Toast
     */
    public body(body: IHtmlValue): this {
        this.options.body = body;
        return this;
    }

    /**
     * Specify a standard icon for this Toast
     * @param iconKey key of the icon information in the translation resources
     */
    public icon(iconKey: string): this {
        this.options.icon = iconKey;
        return this;
    }

    /** Add **CSS** classes to this Toast */
    public class(...classes: string[]): this {
        this.options.classes.push(...classes);
        return this;
    }

    /**
     * Add a progress bar
     * @param steps the number of steps
     * @returns `this`
     */
    public progress(steps: number): this {
        this.options.progress = true;
        this.options.currentStep = 0;
        this.options.totalSteps = steps;
        return this;
    }

    /**
     * Update the current step
     * @param step current step number
     * @returns `this`
     */
    public step(step: number, steps?: number): this {
        this.options.currentStep = step;
        if(steps !== undefined) {
            this.options.totalSteps = steps;
        }
        // update progress
        for(const instance of this.instances) {
            const element = instance.el;
            if(element instanceof HTMLElement) {
                // update CSS variable that can be used to style progress
                element.style.setProperty('--ess-toast-progress', `${this.progressPercent()}`);
                // update progress text
                const progress = element.querySelector('.ess-toast-progress');
                if(progress) {
                    progress.textContent = this.progressText();
                }
            }
        }
        return this;
    }

    /** Add an action to this Toast
     * @param result the result to return if this action is clicked
     * @param text the text of the action's button (or an object providing translation details)
     * @param classes **CSS** classes to apply to the action's button
     */
    public action(result: string, text: string | ITranslation, classes: string[] = []): this {
        if(typeof text === 'string') {
            this.options.actions.push({ result, text, classes });
        }
        else {
            this.options.actions.push({ result, text: translate(this.i18n, text), classes });
        }
        return this;
    }

    /**
     * The default result to return when this Toast is dismissed or disappears. (*default* `'dismiss'`)
     */
    public defaultResult(result: string): this {
        this.options.defaultResult = result;
        return this;
    }

    /**
     * Toggle the visibility of the dismiss button on this Toast (*default* `true`)
     */
    public dismissButton(show: boolean): this {
        this.options.dismissButton = show;
        return this;
    }

    /**
     * Set the duration in milliseconds that this Toast should remain visible (*default* `5000`ms)
     * @param milliseconds duration in milliseconds
     */
    public duration(millisecondsOrDuration: number | ToastDuration): this {
        if(typeof millisecondsOrDuration === 'string') {
            this.options.duration = this.durations[millisecondsOrDuration];
        }
        else {
            this.options.duration = millisecondsOrDuration;
        }
        return this;
    }

    /**
     * Toggle whether this Toast message should be duplicated in the `console` log for debugging purposes (*default* `true`)
     * @param enabled `true` to show the message in the log
     */
    public duplicateInLog(enabled: boolean): this {
        this.options.duplicateInLog = enabled;
        return this;
    }

    /**
     * Show the Toast and return a `Promise` that can be used to await the result
     */
    public show(): Promise<string> {
        this.prepareDefaults();

        return new Promise((resolve, reject) => {
            if(this.options.duplicateInLog) {
                const logMessage =
                    (this.options.title ? `${this.options.title}: ` : '') +
                    (this.options.message || this.options.body.html || '');
                switch(this.options.type) {
                    case 'error':
                        this.logger.error(logMessage, this.options);
                        break;
                    case 'warn':
                        this.logger.warn(logMessage, this.options);
                        break;
                    default:
                        this.logger.info(logMessage, this.options);
                        break;
                }
            }

            let instance: M.Toast;
            let result: string = this.options.defaultResult;

            const callback = () => {
                if(this.options.duplicateInLog) {
                    this.logger.debug(`Result: ${result}`, this.options);
                }

                // remove this instance from instances array
                const index = this.instances.indexOf(instance);
                if(index >= 0) {
                    this.instances.splice(index);
                }

                this.result = result;
                resolve(result);
            };

            instance = M.toast({
                activationPercent: Infinity,
                html: this.buildHtml().html,
                displayLength: this.options.duration,
                classes: this.options.classes.join(''),
                completeCallback: callback
            });

            this.instances.push(instance);

            for(const actionElement of instance.el.querySelectorAll('[data-toast-action]')) {
                const action = actionElement.getAttribute('data-toast-action');
                if(action !== null) {
                    const listener = () => {
                        result = action;
                        instance.dismiss();
                    };
                    actionElement.addEventListener('click', listener, { once: true });
                }
            }
        });
    }

    private prepareDefaults(): void {
        if(this.options.title === true) {
            this.title({ key: `ui:toasts.${this.options.type}.title` });
        }
        else if(this.options.title === false) {
            this.options.title = '';
        }
    }

    private buildIcon(result: string[]): void {
        // TODO: put logic for icon lookup in shared function somewhere so ess-icon and this are using the same logic
        const icon = this.options.icon;
        if(this.options.icon) {
            const library = this.i18n.tr(`${icon}.library`);
            const symbol = this.i18n.tr(`${icon}.symbol`);
            const ligature = this.i18n.tr(`${icon}.ligature`);
            result.push(
                `<i class="ess-icon ess-toast-icon ${library} ${symbol}">`,
                ligature,
                '</i>'
            );
        }
    }

    private buildHtml(): IHtmlValue {
        const result: string[] = [];

        //Role set to alert for all toasts for assistive technologies
        result.push('<div role="alert" aria-live="assertive" aria-atomic="true">');

        this.buildIcon(result);
        if(this.options.title) {
            result.push(
                '<span class="ess-toast-title">',
                escapeHtml(this.options.title, HtmlValue.Empty).html,
                '</span> '
            );
        }
        if(this.options.message) {
            result.push(
                '<span class="ess-toast-message">',
                escapeHtml(this.options.message, HtmlValue.Empty).html,
                '</span> '
            );
        }
        if(this.options.body.html) {
            result.push(
                '<span class="ess-toast-body">',
                escapeHtml(this.body, HtmlValue.Empty).html,
                '</span>');
        }
        if(this.options.progress) {
            result.push(
                `<span class="ess-toast-progress">`,
                escapeHtml(this.progressText(), HtmlValue.Empty).html,
                '</span>'
            );
        }

        result.push('</div>');

        for(const action of this.options.actions) {
            result.push(
                '<button data-toast-action="',
                escapeHtml(action.result, HtmlValue.Empty).html,
                '" class="btn-flat toast-action',
                action.classes && action.classes.length > 0 ? ' ' + action.classes.join(' ') : '',
                '">',
                escapeHtml(action.text, HtmlValue.Empty).html,
                '</button>'
            );
        }

        if(this.options.dismissButton) {
            // TODO: get icon out of resources
            result.push(
                '<i data-toast-action="',
                escapeHtml(this.options.defaultResult, HtmlValue.Empty).html,
                '" class="fas fa-xmark"></i>'
            );
        }

        return new HtmlValue(result.join(''));
    }

    private progressPercent(): number {
        if(this.options.totalSteps <= 0) { return 0; }
        return Math.max(0, Math.min(100, Math.floor(100 * this.options.currentStep / this.options.totalSteps)));
    }

    private progressText(): string {
        return this.i18n.tr('ui:toasts.progress', {
            replace: {
                current: this.options.currentStep,
                total: this.options.totalSteps,
                percent: this.options.currentStep / this.options.totalSteps
            }
        });
    }

}
